import FormCardContainer from "@impero-com/common/components/formCardContainer";
import RightSideIcon from "@impero-com/common/components/rightSideIcon";
import passwordSvg from "@impero-com/common/img/password-login.svg";
import { Meta } from "@impero-com/common/utils/header";
import { t } from "@impero-com/common/utils/i18n";
import {
    Alert,
    Button,
    CardFooter,
    FormSubtitle,
    FormTitle,
} from "@impero-com/ui";
import { Space, Spin } from "antd";
import { useLocation } from "react-router-dom";

import type { GetOauthConsentParams } from "api/generated/model";
import {
    useGetOauthConsent,
    usePostOauthConsent,
} from "api/generated/oauth/oauth";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { useOrgUserProfileCookie } from "utils/profileCookie";
import { useOrgDisplayInfo } from "utils/queries/useOrgDisplayInfoSuspenseQuery";

import { parseAuthorizationRequest } from "./authorizationRequest";
import { getOrganizationSelectionUrl } from "./organizationSelectionUrl";

export function OAuthConsentPage() {
    const { search } = useLocation();
    const org = useOrgDisplayInfo();
    const request = parseAuthorizationRequest(search);

    return (
        <FormCardContainer
            rightSide={
                <RightSideIcon
                    icon={passwordSvg}
                    customImages
                    orgCookie={org}
                />
            }
        >
            <Meta title={() => t("OAuth_Title")} />
            <FormTitle title={t("OAuth_Title")} />
            {request ? (
                <ConsentRequest key={search} request={request} />
            ) : (
                <Alert
                    type="error"
                    showIcon
                    message={t("OAuth_InvalidRequest")}
                />
            )}
            <CardFooter>
                <a href={getOrganizationSelectionUrl()}>
                    {t("Login_ChangeOrganization")}
                </a>
            </CardFooter>
        </FormCardContainer>
    );
}

function ConsentRequest({ request }: { request: GetOauthConsentParams }) {
    const org = useOrgDisplayInfo();
    const user = useOrgUserProfileCookie();
    const mcpEnabled = useFeatureFlag("mcp");
    const consent = useGetOauthConsent(request, {
        query: { enabled: mcpEnabled, retry: false, staleTime: 0, gcTime: 0 },
    });
    const decision = usePostOauthConsent({
        mutation: {
            retry: false,
            onSuccess: (response) =>
                window.location.assign(response.data.redirect),
            // Errors are shown inline so the user can retry their decision.
            onError: () => {},
        },
    });
    const error = consent.error ?? decision.error;
    const sessionExpired = error?.response?.status === 401;

    if (!mcpEnabled) {
        return <Alert type="error" showIcon message={t("OAuth_McpDisabled")} />;
    }
    if (consent.isPending) {
        return <Spin />;
    }

    return (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {error && (
                <Alert
                    type="error"
                    showIcon
                    message={
                        sessionExpired
                            ? t("LoginFlow_LoginExpired")
                            : (error.response?.data.error_description ??
                              t("OAuth_RequestFailed"))
                    }
                />
            )}
            {sessionExpired && (
                <Button onClick={() => window.location.reload()}>
                    {t("LoginFlow_LoginNow")}
                </Button>
            )}
            {!sessionExpired && consent.isError && (
                <Button
                    onClick={() => {
                        void consent.refetch();
                    }}
                >
                    {t("OAuth_TryAgain")}
                </Button>
            )}
            {!sessionExpired && consent.isSuccess && (
                <>
                    <FormSubtitle
                        subtitle={t("OAuth_ClientRequest", {
                            client: consent.data.data.clientName,
                            org: org.name,
                        })}
                    />
                    {user && (
                        <p>{t("OAuth_SignedInAs", { email: user.email })}</p>
                    )}
                    <p>{t("OAuth_McpScope")}</p>
                    <p>
                        {t("OAuth_RedirectHost", {
                            host: consent.data.data.redirectHost,
                        })}
                    </p>
                    <Space>
                        <Button
                            variant="primary"
                            disabled={decision.isPending || decision.isSuccess}
                            loading={
                                decision.isPending &&
                                decision.variables?.data.approve
                            }
                            onClick={() =>
                                decision.mutate({
                                    data: { ...request, approve: true },
                                })
                            }
                        >
                            {t("OAuth_Allow")}
                        </Button>
                        <Button
                            disabled={decision.isPending || decision.isSuccess}
                            loading={
                                decision.isPending &&
                                decision.variables?.data.approve === false
                            }
                            onClick={() =>
                                decision.mutate({
                                    data: { ...request, approve: false },
                                })
                            }
                        >
                            {t("OAuth_Deny")}
                        </Button>
                    </Space>
                </>
            )}
        </Space>
    );
}
